// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import type { ViewContext } from '../viewContext';
import { filterRows, total, recovered, sumBy, materialSeries } from '../data';
import { catInfo, jurisName, jurisColour, FATES } from '../domain';
import { histogramBins, sparklineSvg, rateColour } from '../components/charts';
import { escapeHtml, formatTonnes, formatPercent, fyShort } from '../utils/format';

interface XRow { cat: string; state: string; tonnes: number; rate: number; byFate: Map<string, number>; series: Array<number | null> }
type SortKey = 'material' | 'state' | 'tonnes' | 'rate';

export function renderExplorer(root: HTMLElement, ctx: ViewContext): void {
  const d = ctx.data;
  const y = d.latestYear;
  let sortKey: SortKey = 'tonnes';
  let sortDir: 1 | -1 = -1;
  let q = '';
  let binFilter: [number, number] | null = null;

  // build rows: material × state (latest year), non-zero
  const rows: XRow[] = [];
  for (const cat of d.catKeys) {
    for (const st of d.jurisCodes) {
      const sub = filterRows(d.rows, { yr: y, c: cat, j: st });
      const t = total(sub);
      if (t <= 0) continue;
      const rec = recovered(sub);
      const perState = materialSeries(d, cat, st);
      rows.push({
        cat, state: st, tonnes: t, rate: t > 0 ? (rec / t) * 100 : 0,
        byFate: sumBy(sub, (r) => r.f),
        series: perState.map((s) => (s.total > 0 ? s.total : null)),
      });
    }
  }

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">Explore every material in every state</h1>
      <p class="view-sub">One row per material in each state or territory, ${escapeHtml(fyShort(y))}. Search, sort any column, or click a
        bar in the histogram to filter by recovery rate. Click a row for the material's detail.</p>
    </div>
    <div class="grid-2" style="grid-template-columns:minmax(0,1fr) minmax(0,1.3fr);align-items:start">
      <div class="panel">
        <div class="panel-title">Recovery-rate distribution</div>
        <div class="panel-sub">Each bar counts material×state combinations in that recovery-rate band. Click to filter the table; click again to clear.</div>
        <div data-role="hist"></div>
      </div>
      <div class="panel">
        <div class="controls">
          <input class="text-input" data-role="q" type="search" placeholder="Search material or state…" style="flex:1;min-width:120px" aria-label="Search" />
          <button class="chip" data-role="clear" hidden>Clear filter ✕</button>
        </div>
        <div class="result-count" data-role="count"></div>
        <div class="table-scroll">
          <table class="data">
            <thead><tr>
              <th class="sortable" data-sort="material">Material</th>
              <th class="sortable" data-sort="state">State</th>
              <th class="sortable t-num" data-sort="tonnes">Waste</th>
              <th class="sortable t-num" data-sort="rate">Recovered</th>
              <th>Fate mix</th>
              <th>Trend</th>
            </tr></thead>
            <tbody data-role="tbody"></tbody>
          </table>
        </div>
      </div>
    </div>
  `;

  const tbody = root.querySelector('[data-role="tbody"]') as HTMLElement;
  const histEl = root.querySelector('[data-role="hist"]') as HTMLElement;
  const countEl = root.querySelector('[data-role="count"]') as HTMLElement;
  const clearBtn = root.querySelector('[data-role="clear"]') as HTMLButtonElement;

  function fateMix(byFate: Map<string, number>): string {
    const tot = [...byFate.values()].reduce((a, b) => a + b, 0) || 1;
    return `<span class="mini-bar-cell">${FATES.map((f) => { const v = byFate.get(f.key) || 0; return v > 0 ? `<span style="width:${((v / tot) * 100).toFixed(1)}%;background:${f.colour}"></span>` : ''; }).join('')}</span>`;
  }

  function visible(): XRow[] {
    let r = rows;
    if (q) { const s = q.toLowerCase(); r = r.filter((x) => catInfo(x.cat).short.toLowerCase().includes(s) || jurisName(x.state).toLowerCase().includes(s) || x.state.toLowerCase().includes(s)); }
    if (binFilter) r = r.filter((x) => x.rate >= binFilter![0] && (x.rate < binFilter![1] || (binFilter![1] >= 100 && x.rate <= 100)));
    const cmp: Record<SortKey, (a: XRow, b: XRow) => number> = {
      material: (a, b) => catInfo(a.cat).short.localeCompare(catInfo(b.cat).short),
      state: (a, b) => a.state.localeCompare(b.state),
      tonnes: (a, b) => a.tonnes - b.tonnes,
      rate: (a, b) => a.rate - b.rate,
    };
    return [...r].sort((a, b) => cmp[sortKey](a, b) * sortDir);
  }

  function paintTable(): void {
    const vis = visible();
    countEl.textContent = `${vis.length} of ${rows.length} combinations${binFilter ? ` · recovery ${binFilter[0].toFixed(0)}–${binFilter[1].toFixed(0)}%` : ''}`;
    clearBtn.hidden = !binFilter && !q;
    tbody.innerHTML = vis
      .map((x) => `<tr data-open-material="${escapeHtml(x.cat)}">
        <td><span class="name-cell"><span class="sw" style="background:${catInfo(x.cat).colour}"></span>${escapeHtml(catInfo(x.cat).short)}</span></td>
        <td><span class="pill" style="background:${jurisColour(x.state)}">${escapeHtml(x.state)}</span></td>
        <td class="t-num">${formatTonnes(x.tonnes)}</td>
        <td class="t-num"><span class="rate-pill" style="background:${rateColour(x.rate)}">${formatPercent(x.rate, 0)}</span></td>
        <td>${fateMix(x.byFate)}</td>
        <td>${sparklineSvg(x.series, { colour: catInfo(x.cat).colour })}</td>
      </tr>`)
      .join('') || `<tr style="cursor:default"><td colspan="6" class="empty">No matches.</td></tr>`;
    tbody.querySelectorAll('[data-open-material]').forEach((el) => el.addEventListener('click', () => ctx.openMaterial(el.getAttribute('data-open-material') as string)));
    root.querySelectorAll('[data-sort]').forEach((th) => {
      const k = th.getAttribute('data-sort');
      th.textContent = th.textContent!.replace(/ [▲▼]$/, '') + (k === sortKey ? (sortDir === 1 ? ' ▲' : ' ▼') : '');
    });
  }

  function paintHist(): void {
    const bins = histogramBins(rows.map((r) => r.rate), 10, 0, 100);
    const W = 340, H = 150, mB = 22, mT = 6, mL = 4;
    const bw = (W - mL) / bins.length;
    const maxC = Math.max(...bins.map((b) => b.count), 1);
    const bars = bins
      .map((b, i) => {
        const h = (b.count / maxC) * (H - mB - mT);
        const x = mL + i * bw;
        const active = binFilter && binFilter[0] === b.x0;
        return `<g data-bin="${i}" role="button" style="cursor:pointer" data-tip="${escapeHtml(`${b.x0.toFixed(0)}–${b.x1.toFixed(0)}% recovered\n${b.count} combination${b.count === 1 ? '' : 's'}`)}">
          <rect x="${x}" y="${mT}" width="${bw - 2}" height="${H - mB - mT}" fill="transparent"></rect>
          <rect x="${x}" y="${H - mB - h}" width="${bw - 2}" height="${Math.max(0, h)}" rx="2" fill="${rateColour((b.x0 + b.x1) / 2)}" opacity="${active ? 1 : binFilter ? 0.4 : 0.9}"></rect>
          <text x="${x + bw / 2}" y="${H - 8}" text-anchor="middle" style="font-size:9px;fill:var(--text-tertiary)">${b.x0.toFixed(0)}</text>
        </g>`;
      })
      .join('');
    histEl.innerHTML = `<div class="chart-scroll"><svg class="chart" viewBox="0 0 ${W} ${H}" width="100%" height="${H}" preserveAspectRatio="xMinYMin meet" role="img">${bars}</svg></div>`;
    histEl.querySelectorAll('[data-bin]').forEach((el) => el.addEventListener('click', () => {
      const b = bins[Number(el.getAttribute('data-bin'))];
      binFilter = binFilter && binFilter[0] === b.x0 ? null : [b.x0, b.x1];
      paintHist(); paintTable();
    }));
  }

  root.querySelectorAll('[data-sort]').forEach((th) => th.addEventListener('click', () => {
    const k = th.getAttribute('data-sort') as SortKey;
    if (k === sortKey) sortDir = (sortDir === 1 ? -1 : 1); else { sortKey = k; sortDir = k === 'material' || k === 'state' ? 1 : -1; }
    paintTable();
  }));
  let timer: number | undefined;
  (root.querySelector('[data-role="q"]') as HTMLInputElement).addEventListener('input', (e) => {
    window.clearTimeout(timer);
    const v = (e.target as HTMLInputElement).value;
    timer = window.setTimeout(() => { q = v; paintTable(); }, 250);
  });
  clearBtn.addEventListener('click', () => { binFilter = null; q = ''; (root.querySelector('[data-role="q"]') as HTMLInputElement).value = ''; paintHist(); paintTable(); });

  paintHist();
  paintTable();
}
